import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Popover, Modal, notification, Loading } from '@ss/mtd-react';
import { getUrlParams } from '@utils/tools';
import { useRequest } from 'ahooks';
import BasePage from '@src/basePage';
import { PAGE_NAME, SOURCE_TYPE, LX_RESUME, LX_RESUME_EDIT, RESUME_EDITOR_CALLER } from '@common/const';
import { getResumeEnum, saveResume } from '@service/resumeDetail';
import { getIndividualPrivacyStatus } from '@src/service';
import { useLocation } from 'react-router-dom';
import useGo from '@utils/navigate';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '@src/model';
import classNames from 'classnames';
import { LxSiteClick, LxPageView } from '@utils/siteLx';
import { getOffsetSize } from '@utils/getOffsetSize';
import ResumeBanner from './resumeDetailEdit/resumeBanner';
import ResumeDetailView from './resumeDetailView';
import ResumeDetailEdit from './resumeDetailEdit';
import Empty from './resumeDetailView/empty';

import './index.scss';

interface IParams {
  type?: string;
  mode?: string;
}

function ResumeDetail() {
  const location = useLocation();
  const { socialResumeDetail, campusResumeDetail, campusResumeFormRefStore, socialResumeFormRefStore } = useRootStore();
  const { go } = useGo();
  const params: IParams = getUrlParams();
  // 是否勾选个人保护隐私政策
  const [checked, setChecked] = useState<boolean>(false);
  // 简历详情页模式(edit or view)
  const [mode, setMode] = useState<string>(params?.mode ? params.mode : '');
  // 简历类型(campus or social)
  const [type, setType] = useState<string>(params?.type ? params.type : '');
  // 个人隐私提示框是否显示
  const [visible, setVisible] = useState<boolean>(false);

  const [saveLoading, setSaveLoading] = useState(false);

  const [loading, setLoading] = useState(true);

  // 获取当前存在的简历类型
  const { data: resumeEnum, refresh } = useRequest(() => getResumeEnum(), {
    // manual: true,
    onSuccess: (result) => {
      if (!type && !mode) {
        if (result?.socialResume && result?.campusResume) {
          setMode('view');
          setType('campus');
        }
        if (result?.socialResume && !result?.campusResume) {
          setMode('view');
          setType('social');
        }
        if (!result?.socialResume && result?.campusResume) {
          setMode('view');
          setType('campus');
        }
      }
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    refresh();
    if (type && mode) go(location?.pathname, { type, mode });
  }, [type, mode]);

  // 个人保护法文案获取
  const { data: privacyPolicy = { content: '', status: false } } = useRequest(getIndividualPrivacyStatus);

  /* 保存 */
  const handleSave = async (val = 'view') => {
    LxSiteClick({ bid: LX_RESUME_EDIT.resumeEditSave, valLab: { resumeType: type } });
    setSaveLoading(true);
    let storeRef;
    let store;
    let sourceType;

    if (type === 'campus') {
      storeRef = campusResumeFormRefStore;
      store = campusResumeDetail;
      sourceType = SOURCE_TYPE.campus;
    }
    if (type === 'social') {
      storeRef = socialResumeFormRefStore;
      store = socialResumeDetail;
      sourceType = SOURCE_TYPE.social;
    }
    // 勾选隐私保护政策
    if (!checked) {
      setVisible(true);
      setTimeout(() => {
        setVisible(false);
      }, 2000);
      setSaveLoading(false);
      return false;
    }
    // 校验表单
    const res = await storeRef?.validate().then((results) => {
      return results.every((item) => item);
    });
    if (!res) {
      const node = document.getElementsByClassName('has-error')[0];
      // @ts-ignore
      node?.offsetTop && window.scrollTo({ top: getOffsetSize(node).y - 100, behavior: 'smooth' });
      notification.error({
        title: '错误',
        message: '简历填写有误，请检查',
      });
      setSaveLoading(false);
      return false;
    }

    // 获取表单当前的值
    const param = store.getAllData();
    try {
      await saveResume({ ...param, source: sourceType });
      notification.success({
        title: '成功',
        message: '简历保存成功',
      });
      setLoading(true);
      setMode(val);
      setSaveLoading(false);
      return true;
    } catch (error) {
      setSaveLoading(false);
      return false;
    }
  };

  // 个保法弹框
  const showPrivacyModal = () => {
    const modal = Modal.confirm({
      className: 'common_modal ',
      style: { width: 640, maxHeight: 340 },
      title: '个人信息保护隐私政策',
      closable: true,
      // eslint-disable-next-line react/no-danger
      message: <div className="privacy" dangerouslySetInnerHTML={{ __html: privacyPolicy.content }} />,
      cancelText: '取消',
      cancelBtnProps: { size: 'large' },
      okBtnProps: { type: 'warning', size: 'large', style: { width: 152 } },
      okText: '我已阅读并同意',
      onOk: async () => {
        setChecked(true);
        modal.close();
      },
    });
  };

  // 选择求职状态弹框
  let selectModel;
  const selectTYpe = () => {
    LxSiteClick({ bid: LX_RESUME.resumeCreate });
    // LxPageView({ cid: LX_RESUME.typeSelectView });
    selectModel = Modal.confirm({
      className: 'select_type_modal',
      closable: true,
      style: { width: 640, minHeight: 224 },
      title: '请选择你的求职状态',
      cancelText: false,
      okText: false,
      message: (
        <div className="select_modal_content">
          <div
            className="campus_button"
            onClick={() => {
              LxSiteClick({ bid: LX_RESUME.selectCampus });
              selectModel.close();
              setLoading(true);
              setType('campus');
              setMode('edit');
              setChecked(false);
            }}
          >
            <img src={require('@imgs/education.png')} alt="" />
            <div>在校学生</div>
          </div>
          <div
            className="social_button"
            onClick={() => {
              LxSiteClick({ bid: LX_RESUME.selectSocial });
              selectModel.close();
              setLoading(true);
              setType('social');
              setMode('edit');
              setChecked(false);
            }}
          >
            <img src={require('@imgs/briefcase.png')} alt="" />
            <div>职场人士</div>
          </div>
        </div>
      ),
    });
  };
  // 取消编辑弹框
  const handleCancel = () => {
    Modal.confirm({
      className: 'common_modal',
      style: { width: 430, minHeight: 196 },
      title: '取消编辑',
      closable: true,
      message: '确定取消吗？现在取消将丢失当前页面信息',
      cancelBtnProps: { size: 'large' },
      okBtnProps: { type: 'primary', size: 'large' },
      cancelText: '返回',
      onOk: async () => {
        setMode('view');
      },
    });
  };

  // 加载中
  if (loading) {
    return (
      <div className="page_resume_detail_loading">
        <Loading size="large" message="加载中" direction="vertical" />
      </div>
    );
  }

  // 不存在简历，并且没有参数，则展示空页面
  if (!mode && !type && !resumeEnum?.campusResume && !resumeEnum?.socialResume) {
    LxPageView({ cid: LX_RESUME.emptyView });
    return <Empty text="你当前还没有创建简历" buttonText="立即创建" onclick={selectTYpe} />;
  }

  return (
    <>
      <div
        className={classNames('page_resume_detail', {
          // eslint-disable-next-line camelcase
          page_resume_detail_mode_edit: mode === 'edit',
        })}
      >
        <div className="resume_banner ">
          <ResumeBanner
            mode={mode}
            type={type}
            setMode={setMode}
            resumeEnum={resumeEnum}
            setType={setType}
            save={handleSave}
            setChecked={setChecked}
            setLoading={setLoading}
          />
        </div>

        <div className="resume_main">
          {mode === 'edit' && (
            <ResumeDetailEdit
              type={type}
              setChecked={setChecked}
              resumeEnum={resumeEnum}
              caller={RESUME_EDITOR_CALLER.resumeDetail}
            />
          )}
          {mode === 'view' && (
            <ResumeDetailView
              type={type}
              setMode={setMode}
              setType={setType}
              selectTYpe={selectTYpe}
              resumeEnum={resumeEnum}
            />
          )}
        </div>
      </div>
      {mode === 'edit' && (
        <div className="resume_detail-footer">
          <div className="footer_main">
            <Popover
              className="resume_detail_popover"
              content="你需要阅读并同意《个人信息保护隐私政策》"
              visible={visible}
            >
              <div className="popover_position"> </div>
            </Popover>
            <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
              我已阅读并同意
            </Checkbox>
            <span
              className="info"
              onClick={(e) => {
                e.stopPropagation();
                showPrivacyModal();
              }}
            >
              《个人信息保护隐私政策》
            </span>

            <Button
              className="cancel_btn button"
              onClick={() => {
                handleCancel();
              }}
            >
              取消
            </Button>
            <Button
              loading={saveLoading}
              className="save_btn button"
              onClick={() => {
                handleSave();
              }}
            >
              保存
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default BasePage(observer(ResumeDetail), PAGE_NAME.resumeDetail);
